import { Grid, GridColumn, scrollToElement, TableCell, TableCollapseTrigger } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';

import { Line, LineGroup } from '../../../stores/models/InvoiceDetails';
import LayoutStore from '../../../stores/LayoutStore';
import TableCollapseWithAnalytics from '../../common/TableCollapseWithAnalytics';
import InvoiceDetailsTableViewStore from '../../../stores/view/InvoiceDetailsTableViewStore';
import InvoicePriceStore from '../../../stores/InvoicePriceStore';
import InvoiceLineUsagesTable from './InvoiceLineUsagesTable';

interface IInvoiceDetailsTableProps {
  invoiceDetailsTableViewStore?: InvoiceDetailsTableViewStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  translateStore?: ITranslateStore;
  invoicePriceStore?: InvoicePriceStore;
  layoutStore?: LayoutStore;
}

@inject('invoiceDetailsTableViewStore', 'invoiceDetailsStore', 'translateStore', 'invoicePriceStore', 'layoutStore')
@observer
export default class InvoiceDetailsTable extends React.Component<IInvoiceDetailsTableProps> {
  public componentDidMount(): void {
    setTimeout(() => {
      scrollToElement(document.getElementById(this.props.invoiceDetailsStore.activeLineUsagesId));
    }, 0);
  }

  public render(): JSX.Element {
    const invoiceDetails = this.props.invoiceDetailsStore.invoiceDetails.payload.details;

    return (
      <TableCollapseWithAnalytics
        gaEvent="Accordion - Invoice row"
        tableCollapseProps={{
          hover: true,
          openItems: this.props.invoiceDetailsStore.openLines,
          onToggle: (openItems) => this.props.invoiceDetailsStore.setOpenLines(openItems),
          onRowOpen: (id) => this.props.invoiceDetailsStore.setLastOpenedLine(id)
        }}
      >
        {!this.props.layoutStore.isMobile && <thead>{this.renderTableHeadings()}</thead>}
        <tbody>
          {invoiceDetails.lineGroups !== null
            ? invoiceDetails.lineGroups.map((lineGroup, lineGroupIndex) => this.renderLineGroup(lineGroup, [lineGroupIndex]))
            : invoiceDetails.lines.map((line, lineIndex) => this.renderInvoiceLine(line, [lineIndex]))}
        </tbody>
      </TableCollapseWithAnalytics>
    );
  }

  private renderTableHeadings(): JSX.Element {
    const translate = this.props.translateStore.translate;
    const showPriceWithoutVat = this.props.invoicePriceStore.showPriceWithoutVat;
    const tableColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableColumnWidthByColumnIndex;
    const tableLastColumnGridColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableLastColumnGridColumnWidthByColumnIndex;

    return (
      <tr>
        <TableCell type="th" width={tableColumnWidthByColumnIndex(0)}>
          {translate('shared.invoice-details-modal.service')}
        </TableCell>
        <TableCell type="th" width={tableColumnWidthByColumnIndex(1)}>
          {translate('shared.invoice-details-modal.time')}
        </TableCell>
        <TableCell type="th" width={tableColumnWidthByColumnIndex(2)}>
          {translate('shared.invoice-details-modal.duration-volume')}
        </TableCell>
        <TableCell type="th" width={tableColumnWidthByColumnIndex(3)}>
          <Grid verticalGutter="none" gutter="xsmall" align={['between-xs']}>
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="text-right">
              {showPriceWithoutVat ? translate('shared.invoice-details-modal.price-without-vat') : translate('shared.invoice-details-modal.price-with-vat')}
            </GridColumn>
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(1)} />
          </Grid>
        </TableCell>
      </tr>
    );
  }

  private renderLineGroup(lineGroup: LineGroup, nestingIndexes: number[]): JSX.Element[] {
    const dataRowTrigger: string = `details-row-${nestingIndexes.join('-')}`;
    return [
      <tr id={`invoice-${dataRowTrigger}`} key={`${dataRowTrigger}-row1`}>
        {this.props.invoiceDetailsTableViewStore.filterColumnsByLayout(this.getLineGroupRowColumns(lineGroup, nestingIndexes))}
      </tr>,
      <tr id={dataRowTrigger} key={`${dataRowTrigger}-row2`} data-table-collapse={true}>
        {this.props.invoiceDetailsStore.openLines.includes(dataRowTrigger) && (
          <TableCell colSpan={this.props.invoiceDetailsTableViewStore.getColSpan()} padding="none">
            <TableCollapseWithAnalytics
              gaEvent="Accordion - Invoice row"
              tableCollapseProps={{
                openItems: this.props.invoiceDetailsStore.openLines,
                onToggle: (openItems) => this.props.invoiceDetailsStore.setOpenLines(openItems),
                onRowOpen: (id) => this.props.invoiceDetailsStore.setLastOpenedLine(id)
              }}
            >
              <tbody>
                {lineGroup.lineGroups !== null
                  ? lineGroup.lineGroups.map((childLineGroup, childIndex) => {
                      return this.renderLineGroup(childLineGroup, nestingIndexes.concat(childIndex));
                    })
                  : lineGroup.lines.map((childLine, childIndex) => {
                      return this.renderInvoiceLine(childLine, nestingIndexes.concat(childIndex));
                    })}
              </tbody>
            </TableCollapseWithAnalytics>
          </TableCell>
        )}
      </tr>
    ];
  }

  private getLineGroupRowColumns(lineGroup: LineGroup, nestingIndexes: number[]): JSX.Element[] {
    const showPriceWithoutVat = this.props.invoicePriceStore.showPriceWithoutVat;
    const dataRowTrigger: string = `details-row-${nestingIndexes.join('-')}`;
    const indentLevel = this.props.invoiceDetailsStore.getIndentLevel(nestingIndexes);
    const tableColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableColumnWidthByColumnIndex;
    const tableLastColumnGridColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableLastColumnGridColumnWidthByColumnIndex;
    const lineGroupNameSuffix = this.props.invoiceDetailsTableViewStore.toggleableLineGroupNameSuffix(lineGroup);

    return [
      <TableCell key="both-0" indentLevel={indentLevel} width={tableColumnWidthByColumnIndex(0)} headless={true}>
        <TableCollapseTrigger text={lineGroup.name + lineGroupNameSuffix} url={`#${dataRowTrigger}`} isUnderlined={false} link={true} type="text-color" />
      </TableCell>,
      <TableCell key="desktop-1" width={tableColumnWidthByColumnIndex(1)} />,
      <TableCell key="desktop-2" width={tableColumnWidthByColumnIndex(2)} />,
      <TableCell key="both-3" width={tableColumnWidthByColumnIndex(3)}>
        <Grid verticalGutter="none" gutter="xsmall" align={['between-xs']}>
          {showPriceWithoutVat ? (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="text-right text-bold">
              {formatAsCurrency(lineGroup.groupSum.groupSumWoVat)}
            </GridColumn>
          ) : (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="c-brand-primary-text text-bold text-right">
              {formatAsCurrency(lineGroup.groupSum.groupTotalSum)}
            </GridColumn>
          )}
          <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(1)} align={['middle-xs']}>
            <TableCollapseTrigger
              url={`#${dataRowTrigger}`}
              text="Toggle"
              isUnderlined={false}
              link={true}
              verticalAlign="middle"
              type="text-color"
              icon="#arrow-down"
            />
          </GridColumn>
        </Grid>
      </TableCell>
    ];
  }

  private renderInvoiceLine(invoiceLine: Line, nestingIndexes: number[]) {
    const shouldShowUsages = this.props.invoiceDetailsTableViewStore.isAllowedToShowUsages && this.props.invoiceDetailsTableViewStore.hasUsages(invoiceLine);

    return shouldShowUsages ? this.toggleableLine(invoiceLine, nestingIndexes) : this.staticLine(invoiceLine, nestingIndexes);
  }

  private toggleableLine(toggleableLine: Line, nestingIndexes: number[]): JSX.Element[] {
    const dataRowTrigger: string = `details-row-${nestingIndexes.join('-')}-usages`;

    return [
      <tr id={`invoice-${dataRowTrigger}`} key={`${dataRowTrigger}-row1`}>
        {this.props.invoiceDetailsTableViewStore.filterColumnsByLayout(this.getToggleableLineColumns(nestingIndexes, toggleableLine))}
      </tr>,
      <tr id={dataRowTrigger} key={`${dataRowTrigger}-row2`} data-table-collapse={true}>
        <TableCell colSpan={this.props.invoiceDetailsTableViewStore.getColSpan()} padding="none">
          <InvoiceLineUsagesTable lineId={toggleableLine.lineId} nestingIndexes={nestingIndexes} />
        </TableCell>
      </tr>
    ];
  }

  private getToggleableLineColumns(nestingIndexes: number[], toggleableLine: Line): JSX.Element[] {
    const showPriceWithoutVat = this.props.invoicePriceStore.showPriceWithoutVat;
    const dataRowTrigger: string = `details-row-${nestingIndexes.join('-')}-usages`;
    const invoiceDetailsRowId = `invoice-details-row-${nestingIndexes.join('-')}`;
    const indentLevel = this.props.invoiceDetailsStore.getIndentLevel(nestingIndexes);
    const tableColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableColumnWidthByColumnIndex;
    const tableLastColumnGridColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableLastColumnGridColumnWidthByColumnIndex;
    const lineNameSuffix = this.props.invoiceDetailsTableViewStore.toggleableLineNameSuffix(toggleableLine);

    return [
      <TableCell key="both-0" indentLevel={indentLevel} width={tableColumnWidthByColumnIndex(0)}>
        <TableCollapseTrigger
          id={invoiceDetailsRowId}
          url={`#${dataRowTrigger}`}
          text={toggleableLine.lineName + lineNameSuffix}
          className="text-weight-normal"
          isUnderlined={false}
          link={true}
          verticalAlign="middle"
          type="text-color"
          onClick={() => this.props.invoiceDetailsTableViewStore.onToggleableLineClick(toggleableLine, invoiceDetailsRowId)}
        />
      </TableCell>,
      <TableCell key="desktop-1" width={tableColumnWidthByColumnIndex(1)} />,
      <TableCell key="desktop-2" width={tableColumnWidthByColumnIndex(2)}>
        {this.props.invoiceDetailsTableViewStore.formatAsVolume(toggleableLine)}
      </TableCell>,
      <TableCell key="both-3" width={tableColumnWidthByColumnIndex(3)}>
        <Grid verticalGutter="none" gutter="xsmall" align={['between-xs']}>
          {showPriceWithoutVat ? (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="text-right text-weight-normal">
              {formatAsCurrency(toggleableLine.lineSumWoVat)}
            </GridColumn>
          ) : (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="c-brand-primary-text text-bold text-right text-weight-normal">
              {formatAsCurrency(toggleableLine.lineTotalSum)}
            </GridColumn>
          )}
          <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(1)} align={['middle-xs']}>
            <TableCollapseTrigger
              url={`#${dataRowTrigger}`}
              text="Toggle"
              className="text-weight-normal"
              isUnderlined={false}
              link={true}
              verticalAlign="middle"
              type="text-color"
              icon={this.props.invoiceDetailsTableViewStore.getToggleableLineIcon()}
              onClick={() => {
                this.props.invoiceDetailsTableViewStore.onToggleableLineClick(toggleableLine, invoiceDetailsRowId);
              }}
            />
          </GridColumn>
        </Grid>
      </TableCell>
    ];
  }

  private staticLine(staticLine: Line, nestingIndexes: number[]): JSX.Element {
    const dataRow: string = `details-row-${nestingIndexes.join('-')}`;

    return (
      <tr id={`invoice-details-row-${nestingIndexes.join('-')}`} key={`${dataRow}-row1`}>
        {this.props.invoiceDetailsTableViewStore.filterColumnsByLayout(this.getStaticLineColumns(staticLine, nestingIndexes))}
      </tr>
    );
  }

  private getStaticLineColumns(staticLine: Line, nestingIndexes: number[]): JSX.Element[] {
    const showPriceWithoutVat = this.props.invoicePriceStore.showPriceWithoutVat;
    const indentLevel = this.props.invoiceDetailsStore.getIndentLevel(nestingIndexes);
    const tableColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableColumnWidthByColumnIndex;
    const tableLastColumnGridColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableLastColumnGridColumnWidthByColumnIndex;
    return [
      <TableCell key="both-0" indentLevel={indentLevel} width={tableColumnWidthByColumnIndex(0)}>
        {staticLine.lineName}
      </TableCell>,
      <TableCell key="desktop-1" width={tableColumnWidthByColumnIndex(1)} />,
      <TableCell key="desktop-2" width={tableColumnWidthByColumnIndex(2)}>
        {this.props.invoiceDetailsTableViewStore.getFormattedLineDurationOrQuantity(staticLine)}
      </TableCell>,
      <TableCell key="both-3" width={tableColumnWidthByColumnIndex(3)}>
        <Grid verticalGutter="none" gutter="xsmall" align={['between-xs']}>
          {showPriceWithoutVat ? (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="text-right">
              {formatAsCurrency(staticLine.lineSumWoVat)}
            </GridColumn>
          ) : (
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="c-brand-primary-text text-bold text-right text-weight-normal">
              {formatAsCurrency(staticLine.lineTotalSum)}
            </GridColumn>
          )}
          <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(1)} align={['middle-xs']} />
        </Grid>
      </TableCell>
    ];
  }
}
